import { IdeaListItem } from '../../../../apis/idea/idea-api.def.ts'
import Card from 'antd/es/card'
import {
  DownOutlined,
  EditOutlined,
  EyeOutlined,
  InfoCircleFilled,
} from '@ant-design/icons'
import { useAppSelector } from '../../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../../features/setting/setting.slice.ts'
import { useState } from 'react'
import './idea-card.scss'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import IdeyaTypography from '../../../../common/antd/ideya-typography.tsx'
import { IdeaDirectionTag } from '../idea-list-table/idea-direction-tag.tsx'
import { convertPxToNumber } from '../../../../utils/helpers.ts'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { selectIdeaListInput } from '../../../../redux/slices/idea-list-input.slice.ts'
import { useSelector } from 'react-redux'
import { getCurrentQuarterRange } from '../../../../utils/time.ts'
import {
  currencyValueFormatter,
  getCheckStateNumberColor,
  marketDateTimezoneFormatter,
  percentageValueFormatter,
} from '../../../../utils/formatters.ts'
import { ValueFormatterParams } from 'ag-grid-enterprise'
import { IdeaExtraRow } from './idea-card-extra-row.tsx'
import { selectUser } from '../../../../features/user/user.slice.ts'
import {
  canCreateIdeaPermissions,
  hasPermissionAndFirmCheck,
} from '../../../../utils/permissions.ts'
import { IdeaState } from '../../../../enums/management-input.enum.ts'

interface IdeaCardProps {
  idea: IdeaListItem
  onEditIdea: (id: string) => void
  onViewIdea: (id: string) => void
}

export const IdeaCard: React.FC<IdeaCardProps> = ({
  idea,
  onEditIdea,
  onViewIdea,
}) => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState<boolean>(false)
  const ideaListInput = useSelector(selectIdeaListInput)
  const endDate = dayjs(ideaListInput.dateRange[1]).format('YYYY-MM-DD')
  const isQuarterEnd = endDate === getCurrentQuarterRange()[1]
  const isFutureOrToday =
    dayjs(endDate).isSame(dayjs(), 'day') ||
    dayjs(endDate).isAfter(dayjs(), 'day')
  const isEndDateNull = endDate == 'Invalid Date'
  const isHistorical = !isQuarterEnd && !isFutureOrToday && !isEndDateNull
  const userInfo = useSelector(selectUser)
  const liquidateTextColor =
    idea.state === IdeaState.Liquidate ||
    idea.state === IdeaState.PreLiquidate ||
    idea.state === IdeaState.Ignored
      ? themeStyle.colorTextDisabled
      : undefined

  const canEditIdea = hasPermissionAndFirmCheck(
    canCreateIdeaPermissions,
    userInfo.firmId,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )

  const canEdit = idea.state !== 'Liquidate' && idea.canEdit && canEditIdea

  return (
    <Card
      className={'idea-card'}
      styles={{
        body: {
          padding: 0,
          boxShadow:
            '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)',
          border: `1px solid ${themeStyle.colorBorder}`,
          borderRadius: themeStyle.borderRadiusLG,
        },
      }}
    >
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: `${themeStyle.marginXS} ${themeStyle.marginBase}`,
        }}
      >
        <Row justify={'space-between'}>
          <Col>
            <Row
              gutter={[
                convertPxToNumber(themeStyle.marginXXS),
                convertPxToNumber(themeStyle.marginXXS),
              ]}
              style={{
                alignItems: 'baseline',
              }}
            >
              <Col>
                <IdeyaTypography.Text
                  fontWeight={500}
                  style={{ color: liquidateTextColor }}
                >
                  {idea.bbgCompositeKey}
                </IdeyaTypography.Text>
              </Col>
              <Col>
                <IdeaDirectionTag direction={idea.direction} />
              </Col>
              {idea.alerts.length > 0 && (
                <Col>
                  <InfoCircleFilled />
                </Col>
              )}
            </Row>
          </Col>
          <Col>
            <DownOutlined
              style={{
                transition: 'transform 0.2s',
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation()
                setExpanded(!expanded)
              }}
            />
          </Col>
        </Row>
        <Row>
          <IdeyaTypography.Text
            type={'secondary'}
            size={'sm'}
            style={{ color: liquidateTextColor }}
          >
            {idea.tickerName}
          </IdeyaTypography.Text>
        </Row>
        <Row justify={'space-between'}>
          <Col>
            <Row>
              <IdeyaTypography.Text size={'xs'} type={'secondary'}>
                {t('idea_list.idea_table.columns.daily_return_pct')}
              </IdeyaTypography.Text>
            </Row>
            <Row>
              <IdeyaTypography.Text
                style={{
                  color: getCheckStateNumberColor(
                    idea.dailyReturnPct,
                    idea.state
                  ),
                }}
              >
                {isHistorical
                  ? '-'
                  : percentageValueFormatter({
                      value: idea.dailyReturnPct,
                    } as ValueFormatterParams)}
              </IdeyaTypography.Text>
            </Row>
          </Col>
          <Col>
            <Row>
              <IdeyaTypography.Text size={'xs'} type={'secondary'}>
                {t('idea_list.idea_table.columns.daily_pnl')}
              </IdeyaTypography.Text>
            </Row>
            <Row>
              <IdeyaTypography.Text
                style={{
                  color: getCheckStateNumberColor(idea.dailyPnl, idea.state),
                }}
              >
                {currencyValueFormatter(
                  {
                    value: idea.dailyPnl,
                  } as ValueFormatterParams,
                  { decimal: 0, currency: 'USD' }
                )}
              </IdeyaTypography.Text>
            </Row>
          </Col>
          <Col>
            <Row>
              <IdeyaTypography.Text size={'xs'} type={'secondary'}>
                {t('idea_list.idea_table.columns.quarter_pnl')}
              </IdeyaTypography.Text>
            </Row>
            <Row>
              <IdeyaTypography.Text
                style={{
                  color: getCheckStateNumberColor(idea.quarterPnl, idea.state),
                }}
              >
                {isHistorical
                  ? '-'
                  : currencyValueFormatter(
                      {
                        value: idea.quarterPnl,
                      } as ValueFormatterParams,
                      { decimal: 0, currency: 'USD' }
                    )}
              </IdeyaTypography.Text>
            </Row>
          </Col>
        </Row>
      </div>
      <Row
        align={'middle'}
        justify={'center'}
        style={{
          borderTop: `1px solid ${themeStyle.blue8}`,
          borderBottom: expanded ? `1px solid ${themeStyle.blue8}` : '',
        }}
      >
        <Col
          span={12}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4px 0px',
            backgroundColor: themeStyle.blue6,
            borderBottomLeftRadius: expanded ? '' : '6px',
          }}
          onClick={() => onViewIdea(idea.id)}
        >
          <EyeOutlined
            key={'view'}
            style={{
              color: themeStyle.blue10,
              cursor: 'pointer',
            }}
          />
        </Col>
        <Col
          span={12}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderLeft: `1px solid ${themeStyle.blue8}`,
            paddingTop: '4px',
            paddingBottom: '4px',
            backgroundColor: themeStyle.blue6,
            opacity: !canEdit ? 0.3 : '',
            borderBottomRightRadius: expanded ? '' : '6px',
          }}
          onClick={() => {
            if (canEdit) {
              onEditIdea(idea.id)
            }
          }}
        >
          <EditOutlined
            key={'edit'}
            style={{ color: themeStyle.blue10, cursor: 'pointer' }}
          />
        </Col>
      </Row>
      {expanded && (
        <div
          style={{
            padding: `${themeStyle.paddingBase} ${themeStyle.marginBase}`,
            borderRadius: '0 0 8px 8px',
            marginTop: '-1px',
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            boxShadow:
              '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)',
          }}
        >
          <IdeaExtraRow
            label1={t('idea_list.idea_table.columns.ticker_legal_name')}
            value1={idea.tickerLegalName}
            style1={{ fontWeight: 400, color: liquidateTextColor }}
            label2={t('idea_list.idea_table.columns.total_return_pct')}
            value2={
              isHistorical
                ? '-'
                : percentageValueFormatter({
                    value: idea.totalReturnPct,
                  } as ValueFormatterParams)
            }
            style2={{
              color: getCheckStateNumberColor(idea.totalReturnPct, idea.state),
            }}
          />
          <IdeaExtraRow
            label1={t('idea_list.idea_table.columns.current_total_pnl')}
            value1={currencyValueFormatter(
              {
                value: idea.currentTotalPnl,
              } as ValueFormatterParams,
              { decimal: 0, currency: 'USD' }
            )}
            style1={{
              color: getCheckStateNumberColor(idea.currentTotalPnl, idea.state),
            }}
            label2={t('idea_list.idea_table.columns.avg_cost')}
            value2={currencyValueFormatter(
              {
                value: idea.avgCost,
              } as ValueFormatterParams,
              { decimal: 2, currency: idea.tickerCurrency }
            )}
            style2={{
              color: liquidateTextColor,
            }}
          />
          <IdeaExtraRow
            label1={t('idea_list.idea_table.columns.current_market_value')}
            value1={currencyValueFormatter(
              {
                value: idea.currentMarketValue,
              } as ValueFormatterParams,
              { decimal: 0, currency: 'USD' }
            )}
            style1={{ color: liquidateTextColor }}
            label2={t('idea_list.idea_table.columns.pct_gmv')}
            value2={percentageValueFormatter({
              value: idea.pctGMV,
            } as ValueFormatterParams)}
            style2={{
              color: getCheckStateNumberColor(idea.pctGMV, idea.state),
            }}
          />
          <IdeaExtraRow
            label1={t('idea_list.idea_table.columns.shares')}
            value1={idea.shares.toLocaleString()}
            style1={{ color: liquidateTextColor }}
            label2={t('idea_list.idea_table.columns.target_market_value')}
            value2={currencyValueFormatter(
              {
                value: idea.targetMarketValue,
              } as ValueFormatterParams,
              { decimal: 0, currency: 'USD' }
            )}
            style2={{ color: liquidateTextColor }}
          />
          <IdeaExtraRow
            label1={t('idea_list.idea_table.columns.contributor')}
            value1={idea.contributor}
            style1={{ color: liquidateTextColor }}
            label2={t('idea_list.idea_table.columns.firm_name')}
            value2={idea.firmName}
            style2={{ color: liquidateTextColor }}
          />
          <IdeaExtraRow
            label1={t('idea_list.idea_table.columns.duration')}
            value1={idea.duration.toLocaleString()}
            style1={{ color: liquidateTextColor }}
            label2={t('idea_list.idea_table.columns.created_at')}
            value2={marketDateTimezoneFormatter(
              {
                value: idea.createdAt.toString(),
                data: { marketTimeZone: idea.marketTimeZone },
              },
              { showTimezone: true }
            )}
            style2={{ color: liquidateTextColor }}
          />
        </div>
      )}
    </Card>
  )
}
